import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { api, type SystemHealth, type HealthStatus } from "../../api";
import { fadeUp, stagger } from "../../lib/motion";
import { fmtNum, fmtRelativeTime, cn } from "../../lib/format";

const TONE: Record<HealthStatus, { dot: string; ring: string; label: string }> = {
  green: { dot: "bg-emerald-500", ring: "border-emerald-500/30", label: "ok" },
  amber: { dot: "bg-amber-500", ring: "border-amber-500/40", label: "watch" },
  red: { dot: "bg-terracotta", ring: "border-terracotta", label: "red" },
};

function StatusDot({ status, label }: { status: HealthStatus; label?: string }) {
  const t = TONE[status];
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-ink-faint">
      <span className={cn("inline-block w-2 h-2 rounded-full", t.dot)} aria-hidden />
      {label ?? t.label}
    </span>
  );
}

function Sparkline({ values, status }: { values: number[]; status: HealthStatus }) {
  const W = 120;
  const H = 28;
  if (values.length === 0) return <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-7" />;
  const max = Math.max(1, ...values);
  const stroke =
    status === "red" ? "#b8542e" : status === "amber" ? "#d97706" : "#10b981";
  const step = values.length > 1 ? W / (values.length - 1) : W;
  const pts = values
    .map((v, i) => {
      const x = i * step;
      const y = H - 2 - (H - 4) * (v / max);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-7">
      <polyline fill="none" stroke={stroke} strokeWidth="1.5" points={pts} />
    </svg>
  );
}

function PulseCard({
  status,
  title,
  children,
}: {
  status: HealthStatus;
  title: string;
  children: React.ReactNode;
}) {
  const t = TONE[status];
  return (
    <div className={cn("rounded-md p-5 border bg-paper-soft/60", t.ring)}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">{title}</div>
        <StatusDot status={status} />
      </div>
      {children}
    </div>
  );
}

function fmtRate(r: number): string {
  return (r * 100).toFixed(2) + "%";
}

function fmtMs(n: number | null): string {
  return n == null ? "—" : Math.round(n) + " ms";
}

function shortName(fn: string): string {
  return fn.replace(/^language-learner-/, "");
}

export default function SystemPulse() {
  const [data, setData] = useState<SystemHealth | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setData(await api.getSystemHealth());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  // Lazy load on first mount.
  if (data === null && err === null && !loading) {
    void refresh();
  }

  return (
    <motion.section
      initial="hidden"
      animate="show"
      variants={stagger(0.04)}
      data-testid="system-pulse"
      className="mt-12"
    >
      <motion.div variants={fadeUp} className="flex items-baseline justify-between gap-4 mb-6">
        <div>
          <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">infrastructure</div>
          <h3 className="font-serif text-2xl mt-1">System pulse — live from CloudWatch</h3>
          <p className="text-sm text-ink-soft mt-1">
            {data
              ? `Window: last ${Math.round(data.window_seconds / 60)} min · pulled ${fmtRelativeTime(data.generated_at)}${data.cached ? " (cached)" : ""}.`
              : "Pulled on load and on manual refresh — no auto-poll."}
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm transition-colors",
            "border-[rgba(42,36,26,0.18)] hover:border-terracotta hover:text-terracotta",
            loading && "opacity-50 cursor-wait",
          )}
        >
          <RefreshCw size={13} className={cn(loading && "animate-spin")} />
          Refresh
        </button>
      </motion.div>

      {err && (
        <motion.div
          variants={fadeUp}
          className="card border-terracotta/40 text-terracotta flex items-center gap-2 mb-6"
        >
          <AlertTriangle size={14} /> {err}
        </motion.div>
      )}

      {data && (
        <>
          {/* Top-row summary cards */}
          <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PulseCard status={data.tutor_failures.status} title="tutor turn failures">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="font-serif text-3xl">
                    {fmtNum(data.tutor_failures.last_hour_total)}
                  </div>
                  <div className="text-[11px] text-ink-faint mt-0.5">last hour</div>
                  <Sparkline
                    values={data.tutor_failures.sparkline_1h}
                    status={data.tutor_failures.status}
                  />
                </div>
                <div>
                  <div className="font-serif text-3xl">
                    {fmtNum(data.tutor_failures.last_24h_total)}
                  </div>
                  <div className="text-[11px] text-ink-faint mt-0.5">last 24h</div>
                  <Sparkline
                    values={data.tutor_failures.sparkline_24h}
                    status={data.tutor_failures.status}
                  />
                </div>
              </div>
            </PulseCard>

            <PulseCard status={data.openrouter.status} title="openrouter routing">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="font-serif text-3xl">
                    {fmtRate(data.openrouter.default_success_rate)}
                  </div>
                  <div className="text-[11px] text-ink-faint mt-0.5">default success rate</div>
                </div>
                <div>
                  <div className="font-serif text-3xl">
                    {fmtNum(data.openrouter.fallback_firings_last_hour)}
                  </div>
                  <div className="text-[11px] text-ink-faint mt-0.5">together fallback firings</div>
                  <Sparkline
                    values={data.openrouter.fallback_sparkline}
                    status={data.openrouter.status}
                  />
                </div>
              </div>
            </PulseCard>

            <PulseCard status={data.memory_pipeline.status} title="memory pipeline">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="font-serif text-2xl">
                    {fmtNum(data.memory_pipeline.ops_applied_last_hour)}
                  </div>
                  <div className="text-[11px] text-ink-faint mt-0.5">ops applied / 1h</div>
                </div>
                <div>
                  <div className="font-serif text-2xl">
                    {fmtNum(data.memory_pipeline.extractor_invocations_last_hour)}
                  </div>
                  <div className="text-[11px] text-ink-faint mt-0.5">extractor runs / 1h</div>
                </div>
                <div>
                  <div className="font-serif text-2xl">
                    {data.memory_pipeline.avg_ops_per_invocation.toFixed(2)}
                  </div>
                  <div className="text-[11px] text-ink-faint mt-0.5">avg ops / run</div>
                </div>
              </div>
            </PulseCard>

            <PulseCard status={data.api_gateway.status} title="api gateway · /api/*">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="font-serif text-2xl">{fmtRate(data.api_gateway.rate_4xx)}</div>
                  <div className="text-[11px] text-ink-faint mt-0.5">4xx rate</div>
                </div>
                <div>
                  <div className="font-serif text-2xl">{fmtRate(data.api_gateway.rate_5xx)}</div>
                  <div className="text-[11px] text-ink-faint mt-0.5">5xx rate</div>
                </div>
                <div>
                  <div className="font-serif text-2xl">
                    {fmtMs(data.api_gateway.p95_latency_ms)}
                  </div>
                  <div className="text-[11px] text-ink-faint mt-0.5">p95 latency</div>
                </div>
              </div>
              <div className="text-[11px] text-ink-faint mt-2">
                {fmtNum(data.api_gateway.total_requests_last_hour)} requests · last hour
                {!data.api_gateway.configured && " · TutorHttpApi ApiId not yet resolved"}
              </div>
            </PulseCard>
          </motion.div>

          {/* Cron Lambdas */}
          <motion.div variants={fadeUp} className="mt-6 card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">cron lambdas</div>
                <h4 className="font-serif text-xl mt-1">Last successful run</h4>
              </div>
            </div>
            <ul className="divide-y divide-[rgba(42,36,26,0.08)]">
              {data.crons.map((c) => (
                <li
                  key={c.name}
                  className="flex items-center gap-4 py-2.5 text-sm"
                  data-testid={`cron-row-${c.name}`}
                >
                  <StatusDot status={c.status} label="" />
                  <div className="flex-1 min-w-0 font-mono text-[12px]">{shortName(c.name)}</div>
                  <div className="text-xs text-ink-faint shrink-0">
                    every {Math.round(c.interval_seconds / 60)}m
                  </div>
                  <div className="text-xs text-ink-soft shrink-0">
                    {c.last_run_at ? fmtRelativeTime(c.last_run_at) : "never"}
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Lambda invocations + errors */}
          <motion.div variants={fadeUp} className="mt-6 card overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">lambdas · last hour</div>
                <h4 className="font-serif text-xl mt-1">Invocations + errors</h4>
              </div>
            </div>
            <div className="overflow-x-auto -mx-1 px-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] text-ink-faint uppercase tracking-[0.14em]">
                    <th className="text-left py-2 font-normal">function</th>
                    <th className="text-right py-2 font-normal">inv</th>
                    <th className="text-right py-2 font-normal">err</th>
                    <th className="text-right py-2 font-normal">err rate</th>
                    <th className="text-right py-2 font-normal">p95</th>
                    <th className="text-right py-2 font-normal hidden md:table-cell">last seen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(42,36,26,0.08)]">
                  {data.lambdas.map((l) => (
                    <tr
                      key={l.name}
                      className={cn(l.status === "red" && "bg-terracotta/5")}
                      data-testid={`lambda-row-${l.name}`}
                    >
                      <td className="py-2 font-mono text-[12px] flex items-center gap-2">
                        <StatusDot status={l.status} label="" />
                        {shortName(l.name)}
                      </td>
                      <td className="text-right py-2">{fmtNum(l.invocations)}</td>
                      <td className="text-right py-2">{fmtNum(l.errors)}</td>
                      <td className="text-right py-2">{fmtRate(l.error_rate)}</td>
                      <td className="text-right py-2">{fmtMs(l.duration_p95_ms)}</td>
                      <td className="text-right py-2 text-xs text-ink-faint hidden md:table-cell">
                        {l.last_invocation_at ? fmtRelativeTime(l.last_invocation_at) : "never"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </>
      )}
    </motion.section>
  );
}
